import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Check, ChevronRight, Clock, ListChecks, Lock, MessageSquare, Mic, ShieldCheck, Users } from 'lucide-react';
import clsx from 'clsx';
import type { GroupKey, ReportShareResponse, ReportSubmitResponse, StudentTier, TierAnswer } from '@bbl/validation';
import { ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { Drawer } from '../components/ui/Drawer';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { basePath, useReportForm } from '../components/teacher-reports/form/useReportForm';
import { canRecord, VoiceNoteRecorder } from '../components/teacher-reports/form/VoiceNote';
import { ChecklistItem } from '../components/teacher-reports/form/ChecklistItem';
import { StudentGroups } from '../components/teacher-reports/form/StudentGroups';
import { PointsBar, PointsPanel, PointsRing, ScoreLines } from '../components/teacher-reports/form/PointsPanel';
import { CelebrationDialog } from '../components/teacher-reports/form/CelebrationDialog';
import { draftLineLabels } from '../components/teacher-reports/form/scoreWords';
import { Ml, ShowMalayalamContext, useShowMalayalam } from '../components/teacher-reports/form/Malayalam';
import { formatPeriod, formatWhen, KIND_LABEL, MODE_LABEL, timeLeft } from '../components/teacher-reports/format';
import { isReportsDisabled } from '../components/teacher-reports/useDueReports';

export const Route = createFileRoute('/_authed/me/report/$kind/$batchId/$periodKey')({
  component: ReportFormPage,
});

const card = 'rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-4 py-3';
const pill = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap';

function ReportFormPage() {
  const { kind, batchId, periodKey } = Route.useParams();
  const reportRef = useMemo(() => ({ kind, batchId, periodKey }), [kind, batchId, periodKey]);
  const form = useReportForm(reportRef);
  const { toast } = useToast();
  const [shareMsg, setShareMsg] = useState<ReportShareResponse | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [celebration, setCelebration] = useState<ReportSubmitResponse | null>(null);
  const [showMl, setShowMl] = useShowMalayalam();

  const crumbs = (
    <nav aria-label="Breadcrumb" className="mb-1.5 flex flex-wrap items-center gap-1 text-xs text-[color:var(--color-text-muted)]">
      <Link to="/me/reports" className="text-gold-700 hover:underline dark:text-gold-300">
        My reports
      </Link>
      <ChevronRight size={12} aria-hidden="true" />
      <span>{KIND_LABEL[kind as keyof typeof KIND_LABEL] ?? 'Report'}</span>
    </nav>
  );

  if (form.query.isLoading) {
    return (
      <div className="mx-auto w-full max-w-7xl space-y-3 px-4 py-6 sm:px-6 lg:px-8">
        {crumbs}
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (form.query.isError || !form.data || !form.score) {
    const err = form.query.error;
    return (
      <div className="mx-auto w-full max-w-7xl space-y-3 px-4 py-6 sm:px-6 lg:px-8">
        {crumbs}
        {isReportsDisabled(err) ? (
          <EmptyState title="Reports aren’t switched on for you yet" icon={<ListChecks size={32} aria-hidden="true" />} />
        ) : err instanceof ApiError && err.status === 404 ? (
          <EmptyState
            title="No report is due for this period"
            description="Your class timetable doesn’t expect this report — maybe it was a holiday, or the class was cancelled."
            icon={<ListChecks size={32} aria-hidden="true" />}
            action={
              <Link to="/me/reports" className="text-sm font-medium text-gold-700 underline dark:text-gold-300">
                Back to My reports
              </Link>
            }
          />
        ) : (
          <ErrorState title="Could not load this report" description={err instanceof ApiError ? err.message : undefined} onRetry={() => void form.query.refetch()} />
        )}
      </div>
    );
  }

  const d = form.data;
  const t = d.template;
  const score = form.score;
  const status = d.report.status;
  const submitted = status === 'submitted' || status === 'approved';
  const now = form.now;
  const locked = d.window?.status === 'locked';
  const labels = draftLineLabels(d, form.answers, now);
  // Past the due time but still accepted — by the live clock, so it changes without a reload.
  const inGrace = !!d.window && now.getTime() > Date.parse(d.window.dueAt) && now.getTime() <= Date.parse(d.window.graceUntil);
  const lockedLabel = locked && d.window ? `Opens ${formatWhen(d.window.opensAt, now)}` : null;
  const editable = form.editable && !locked;
  const setItem = (key: string, value: boolean | number) => form.update((a) => ({ ...a, items: { ...a.items, [key]: value } }));
  const checkItems = t.items.filter((i) => i.type === 'check' && !d.facts.notApplicable.includes(i.key));

  const onSubmit = async () => {
    try {
      const res = await form.submit.mutateAsync();
      setBreakdownOpen(false);
      setCelebration(res);
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not submit the report.', { variant: 'error' });
    }
  };
  const onShare = async (itemKey: string) => {
    try {
      setShareMsg(await form.share.mutateAsync(itemKey));
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not prepare the message.', { variant: 'error' });
    }
  };
  const onUpload = async (itemKey: string, file: File) => {
    try {
      const res = await form.upload.mutateAsync({ itemKey, file });
      toast(`Attached ${res.fileName ?? file.name}.`, { variant: 'success' });
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Could not attach the file.', { variant: 'error' });
    }
  };

  const heading = `${KIND_LABEL[d.kind]}`;
  const hasMl = t.items.some((i) => i.labelMl || i.hintMl) || t.fields.some((f) => f.labelMl || f.placeholderMl);
  const withMl = (node: ReactNode) => <ShowMalayalamContext.Provider value={showMl}>{node}</ShowMalayalamContext.Provider>;
  const meta = (
    <div className="mt-2 flex flex-wrap gap-1.5">
      <span className={clsx(pill, 'bg-gold-500/15 text-gold-700 dark:text-gold-300')}>{d.batchName}</span>
      <span className={clsx(pill, 'border border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]')}>{MODE_LABEL[d.mode]}</span>
      <span className={clsx(pill, 'border border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]')}>
        {formatPeriod(d.kind, d.periodStart, d.periodKey)}
      </span>
      {!submitted && d.window ? (
        locked ? (
          <span className={clsx(pill, 'border border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]')}>
            <Lock size={11} aria-hidden="true" /> Opens {formatWhen(d.window.opensAt, now)}
          </span>
        ) : inGrace ? (
          <span className={clsx(pill, 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)]')}>
            <AlertTriangle size={11} aria-hidden="true" /> Late — accepted until {formatWhen(d.window.graceUntil, now)}
          </span>
        ) : (
          <span className={clsx(pill, 'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)]')}>
            <Clock size={11} aria-hidden="true" /> Due {formatWhen(d.window.dueAt, now)} · {timeLeft(d.window.dueAt, now)}
          </span>
        )
      ) : null}
      {hasMl ? (
        <button
          type="button"
          aria-pressed={showMl}
          onClick={() => setShowMl(!showMl)}
          className={clsx(pill, 'border', showMl ? 'border-gold-500 bg-gold-500/15 text-gold-700 dark:text-gold-300' : 'border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]')}
        >
          <span lang="ml">മലയാളം</span> · {showMl ? 'Showing Malayalam' : 'Show Malayalam'}
        </button>
      ) : null}
    </div>
  );

  if (submitted) {
    return withMl(
      <div className="mx-auto w-full max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
        {crumbs}
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{heading}</h1>
        {meta}
        <div className="mt-5 space-y-4">
          <div
            className={clsx(
              'flex gap-2.5 rounded-lg border px-3.5 py-3',
              status === 'approved'
                ? 'border-[color:var(--color-success)]/45 bg-[color:var(--color-success)]/[0.08]'
                : 'border-gold-500/50 bg-gold-50/35 dark:bg-gold-700/15',
            )}
          >
            {status === 'approved' ? <Check size={18} aria-hidden="true" /> : <Clock size={18} aria-hidden="true" />}
            <div>
              <p className="font-semibold">
                {status === 'approved'
                  ? `Approved · ${d.report.pointsTotal ?? 0} points banked`
                  : `Submitted ${d.report.submittedAt ? formatWhen(d.report.submittedAt, now) : ''} · awaiting approval`}
              </p>
              <p className="text-sm text-[color:var(--color-text-muted)]">
                {status === 'approved'
                  ? 'They count toward this month’s incentive.'
                  : 'Your Nalim usually reviews within a day. You’ll get a notification when your points bank.'}
              </p>
            </div>
          </div>
          <section className={card}>
            <h2 className="mb-2 text-sm font-semibold">Points</h2>
            <div className="flex items-center gap-4">
              <PointsRing total={d.report.pointsTotal ?? 0} max={t.maxPoints} size={88} />
              {d.report.pointsLines ? (
                <ul className="flex-1">
                  {d.report.pointsLines.map((l) => (
                    <li key={l.key} className="flex justify-between border-b border-dashed border-[color:var(--color-border)] py-1.5 text-[13px] last:border-b-0">
                      <span>{l.label}</span>
                      <span className="font-bold tabular-nums">{l.points ? `+${l.points}` : '0'}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
          <section className={card}>
            <h2 className="mb-2 text-sm font-semibold">What you reported</h2>
            <ul>
              {score.items.map((i) => {
                const item = t.items.find((x) => x.key === i.key)!;
                return (
                  <li key={i.key} className="flex items-center gap-2 py-1 text-sm">
                    <span
                      className={clsx(
                        'inline-flex h-[18px] w-[18px] items-center justify-center rounded text-[11px] font-bold',
                        i.done ? 'bg-[color:var(--color-success)] text-white' : 'bg-[color:var(--color-surface-strong)] text-[color:var(--color-text-muted)]',
                      )}
                      aria-hidden="true"
                    >
                      {i.done ? '✓' : '–'}
                    </span>
                    <span className="sr-only">{i.done ? 'Done:' : 'Not done:'}</span>
                    {item.label}
                  </li>
                );
              })}
            </ul>
          </section>
          {d.voiceNote && !d.voiceNote.deleted ? (
            <section className={card}>
              <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold">
                <Mic size={16} aria-hidden="true" /> Your voice note
              </h2>
              <VoiceNoteRecorder
                basePath={basePath(reportRef)}
                note={d.voiceNote}
                editable={false}
                retentionDays={d.voiceNoteRetentionDays}
                onSaved={() => void form.query.refetch()}
              />
            </section>
          ) : null}
          <Link to="/today" className="inline-flex h-10 items-center rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 text-sm font-semibold hover:border-gold-500">
            Back to Today
          </Link>
        </div>
        {/* The form reloads as "submitted" right after submit — keep the celebration on top of it. */}
        <CelebrationDialog result={celebration} title={`${KIND_LABEL[d.kind]} · ${d.batchName}`} onClose={() => setCelebration(null)} />
      </div>
    );
  }

  return withMl(
    <div className="mx-auto w-full max-w-7xl px-4 pb-28 pt-6 sm:px-6 lg:px-8 lg:pb-12">
      {crumbs}
      <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{heading}</h1>
      {meta}

      <div className="mt-5 grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {status === 'changes_requested' ? (
            <div className="flex gap-2.5 rounded-lg border border-[color:var(--color-warning)]/45 bg-[color:var(--color-warning)]/[0.08] px-3.5 py-3">
              <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
              <div>
                <p className="font-semibold">Your Nalim asked for changes</p>
                {d.report.reviewNote ? <p className="text-sm">“{d.report.reviewNote}”</p> : null}
                <p className="text-xs text-[color:var(--color-text-muted)]">
                  {!d.report.resubmitBy
                    ? ''
                    : Date.parse(d.report.resubmitBy) < now.getTime()
                      ? 'The time to keep your on-time status has passed. You can still resubmit — it will count as late.'
                      : `Resubmit by ${formatWhen(d.report.resubmitBy, now)} to keep your on-time status.`}
                </p>
              </div>
            </div>
          ) : null}
          {locked && d.window ? (
            <div className="flex gap-2.5 rounded-lg border border-gold-500/50 bg-gold-50/35 px-3.5 py-3 dark:bg-gold-700/15">
              <Lock size={18} className="shrink-0" aria-hidden="true" />
              <div>
                <p className="font-semibold">
                  Opens {formatWhen(d.window.opensAt, now)} — {d.window.opensBecause}
                </p>
                <p className="text-sm text-[color:var(--color-text-muted)]">
                  You can look through it now; you can fill and submit it once it opens. Due {formatWhen(d.window.dueAt, now)}.
                </p>
              </div>
            </div>
          ) : null}

          <section className={clsx(card, 'text-center')}>
            <p className="text-xl text-gold-700 dark:text-gold-300" lang="ar" dir="rtl">
              بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِيْمِ
            </p>
            <p className="text-xs text-[color:var(--color-text-muted)]">
              May Allah accept our efforts and make this {d.kind === 'daily' ? 'day' : d.kind === 'weekly' ? 'week' : 'month'} beneficial.
            </p>
          </section>

          <section className={card}>
            <header className="mb-1 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <ShieldCheck size={16} aria-hidden="true" /> From the portal
              </h2>
              <span className={clsx(pill, 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]')}>
                <Lock size={11} aria-hidden="true" /> Auto-filled
              </span>
            </header>
            <p className="mb-2.5 text-xs text-[color:var(--color-text-muted)]">
              Filled in from attendance, marks and fees — nothing to type. If a number is wrong, fix it at the source.
            </p>
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {d.facts.metrics.map((m) => (
                <div key={m.key} className="min-w-0 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-2.5 py-2">
                  <dt className="text-[11px] text-[color:var(--color-text-muted)]">{m.label}</dt>
                  <dd className="mt-0.5 truncate font-bold tabular-nums" title={m.value}>
                    {m.value}
                  </dd>
                  <dd className="mt-0.5 flex items-center gap-1 text-[10px] text-[color:var(--color-text-subtle)]">
                    <ShieldCheck size={10} aria-hidden="true" /> {m.source}
                  </dd>
                </div>
              ))}
            </dl>
          </section>

          <section className={card}>
            <header className="mb-2 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <ListChecks size={16} aria-hidden="true" />
                {d.kind === 'daily' ? 'Daily discipline' : d.kind === 'weekly' ? 'This week' : 'This month'}
                <span className="font-medium tabular-nums text-[color:var(--color-text-muted)]">
                  {score.itemsDone}/{score.itemsCount}
                </span>
              </h2>
              {editable && checkItems.length > 0 ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Check size={14} aria-hidden="true" />}
                  onClick={() => {
                    form.update((a) => ({ ...a, items: { ...a.items, ...Object.fromEntries(checkItems.map((i) => [i.key, true])) } }));
                    toast('Ticked every checklist item — untick anything you didn’t do.', { variant: 'info' });
                  }}
                >
                  Mark all done
                </Button>
              ) : null}
            </header>
            <div className="space-y-2">
              {t.items.map((item) => (
                <ChecklistItem
                  key={item.key}
                  item={item}
                  value={form.answers.items[item.key]}
                  facts={d.facts}
                  editable={editable}
                  busy={form.share.isPending || form.upload.isPending}
                  onToggle={() => setItem(item.key, form.answers.items[item.key] !== true)}
                  onCount={(n) => setItem(item.key, n)}
                  onShare={() => void onShare(item.key)}
                  onUpload={(file) => void onUpload(item.key, file)}
                  fileHref={form.fileUrl(item.key)}
                />
              ))}
            </div>
          </section>

          <section className={card}>
            <header className="mb-1 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Users size={16} aria-hidden="true" /> Student groups
                <span className="font-medium text-[color:var(--color-text-muted)]">Top · Medium · Average</span>
              </h2>
              <span className={clsx(pill, 'border border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]')}>+{t.groupsPoints} points</span>
            </header>
            <p className="mb-2.5 text-xs text-[color:var(--color-text-muted)]">
              The portal places students in these groups from their recent exam marks, quizzes or attendance{t.groups.withPercent ? ' (each student’s % is shown)' : ''}. Tell us how you’re supporting each group — and if you know a student belongs elsewhere this time, change their group below.
            </p>
            <StudentGroups
              template={t}
              facts={d.facts}
              answers={form.answers}
              editable={editable}
              onChange={(group: GroupKey, next: TierAnswer) => form.update((a) => ({ ...a, groups: { ...a.groups, [group]: next } }))}
              onPlace={(studentId: string, group: StudentTier | null) =>
                form.update((a) => {
                  const { [studentId]: _was, ...rest } = a.placements ?? {};
                  const placements = group ? { ...rest, [studentId]: group } : rest;
                  const { placements: _old, ...without } = a;
                  return Object.keys(placements).length > 0 ? { ...without, placements } : without;
                })
              }
            />
          </section>

          {t.fields.length > 0 ? (
            <section className={card}>
              <header className="mb-2 flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <MessageSquare size={16} aria-hidden="true" />
                  {d.kind === 'monthly' ? 'Reflections' : d.kind === 'weekly' ? 'For management' : 'Support & remarks'}
                </h2>
                {t.reflectPoints ? (
                  <span className={clsx(pill, 'border border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted)]')}>+{t.reflectPoints} points</span>
                ) : (
                  <span className="text-xs text-[color:var(--color-text-muted)]">Optional</span>
                )}
              </header>
              <div className="space-y-3.5">
                {t.fields.map((f) =>
                  f.type === 'students' ? (
                    <div key={f.key}>
                      <p className="mb-1.5 text-sm font-semibold">
                        {f.label}
                        <Ml text={f.labelMl} className="block text-sm font-normal text-[color:var(--color-text-muted)]" />
                      </p>
                      <div className="flex flex-wrap gap-1.5" role="group" aria-label={f.label}>
                        {d.facts.students.map((s) => {
                          const list = Array.isArray(form.answers.fields[f.key]) ? (form.answers.fields[f.key] as string[]) : [];
                          const pressed = list.includes(s.id);
                          return (
                            <button
                              key={s.id}
                              type="button"
                              aria-pressed={pressed}
                              disabled={!editable}
                              onClick={() =>
                                form.update((a) => ({
                                  ...a,
                                  fields: { ...a.fields, [f.key]: pressed ? list.filter((x) => x !== s.id) : [...list, s.id] },
                                }))
                              }
                              className={clsx(
                                'inline-flex min-h-8 items-center rounded-full border px-3 text-xs font-medium',
                                pressed ? 'border-gold-500 bg-gold-500/15 font-semibold' : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] hover:border-gold-500',
                              )}
                            >
                              {s.name}
                            </button>
                          );
                        })}
                        {d.facts.students.length === 0 ? <span className="text-xs text-[color:var(--color-text-muted)]">No students enrolled yet.</span> : null}
                      </div>
                    </div>
                  ) : (
                    <label key={f.key} className="block">
                      <span className="mb-1.5 block text-sm font-semibold">
                        {f.label}
                        <Ml text={f.labelMl} className="block text-sm font-normal text-[color:var(--color-text-muted)]" />
                      </span>
                      <textarea
                        rows={3}
                        disabled={!editable}
                        value={typeof form.answers.fields[f.key] === 'string' ? (form.answers.fields[f.key] as string) : ''}
                        onChange={(e) => form.update((a) => ({ ...a, fields: { ...a.fields, [f.key]: e.target.value } }))}
                        placeholder={showMl && f.placeholderMl ? f.placeholderMl : f.placeholder}
                        className="w-full resize-y rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500 disabled:opacity-70"
                      />
                    </label>
                  ),
                )}
                {t.fields.some((f) => f.reflect) ? (
                  <p className="text-xs text-[color:var(--color-text-muted)]">A sentence or two each is enough — both reflections together earn the points.</p>
                ) : null}
              </div>
            </section>
          ) : null}

          {t.voiceNote && (canRecord() || d.voiceNote) ? (
            <section className={card}>
              <header className="mb-1 flex items-center justify-between gap-2">
                <h2 className="flex items-center gap-2 text-sm font-semibold">
                  <Mic size={16} aria-hidden="true" /> Voice note for your Nalim
                </h2>
                <span className="text-xs text-[color:var(--color-text-muted)]">Optional · no points</span>
              </header>
              <p className="mb-2.5 text-xs text-[color:var(--color-text-muted)]">
                Anything else you’d like to say about this {d.kind === 'daily' ? 'day' : d.kind === 'weekly' ? 'week' : 'month'}? Say it instead of typing.
              </p>
              <VoiceNoteRecorder
                basePath={basePath(reportRef)}
                note={d.voiceNote}
                editable={editable && canRecord()}
                retentionDays={d.voiceNoteRetentionDays}
                onSaved={() => void form.query.refetch()}
              />
            </section>
          ) : null}

          <p className="flex items-center gap-1.5 text-xs text-[color:var(--color-text-muted)]" aria-live="polite">
            <Check size={12} aria-hidden="true" />
            {!form.canSave
              ? 'Your answers save automatically once the report opens.'
              : form.saveState === 'saving'
                ? 'Saving…'
                : form.saveState === 'error'
                  ? 'Couldn’t save — we’ll keep trying. Your answers are kept on this device.'
                  : 'Draft saved automatically · you can finish later on any device.'}
          </p>
        </div>

        <aside className="hidden lg:sticky lg:top-[72px] lg:block" aria-label="Points for this report">
          <PointsPanel score={score} labels={labels} canSubmit={d.canSubmit} lockedLabel={lockedLabel} submitting={form.submit.isPending} onSubmit={() => void onSubmit()} />
        </aside>
      </div>

      <PointsBar
        score={score}
        canSubmit={d.canSubmit}
        submitting={form.submit.isPending}
        onSubmit={() => void onSubmit()}
        onBreakdown={() => setBreakdownOpen(true)}
      />

      <Drawer open={breakdownOpen} onClose={() => setBreakdownOpen(false)} title={`${score.total} of ${score.max} points`} size="sm">
        <ScoreLines score={score} labels={labels} />
        <p className="text-xs text-[color:var(--color-text-muted)]">Points stay pending until your Nalim approves the report.</p>
        {d.canSubmit ? (
          <button
            type="button"
            onClick={() => void onSubmit()}
            className="mt-4 inline-flex h-11 w-full items-center justify-center rounded-md bg-gradient-to-b from-gold-100 to-gold-500 font-semibold text-ink-900"
          >
            Submit report
          </button>
        ) : null}
      </Drawer>

      <Drawer open={shareMsg !== null} onClose={() => setShareMsg(null)} title="Share on WhatsApp" size="sm">
        {shareMsg ? <ShareBody message={shareMsg.message} onDone={() => setShareMsg(null)} /> : null}
      </Drawer>

      <CelebrationDialog result={celebration} title={`${KIND_LABEL[d.kind]} · ${d.batchName}`} onClose={() => setCelebration(null)} />
    </div>
  );
}

function ShareBody({ message, onDone }: { message: string; onDone: () => void }) {
  const [text, setText] = useState(message);
  return (
    <div className="space-y-3">
      <p className="text-sm text-[color:var(--color-text-muted)]">Written for you — fill in the blanks or edit anything before sending.</p>
      <textarea
        rows={9}
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="Message"
        className="w-full rounded-lg border border-[#bfe5cf] bg-[#e7f7ee] px-3 py-2.5 text-sm text-[#0e3b24] dark:border-[#1f4a31] dark:bg-[#12301f] dark:text-[#d6f2e1]"
      />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          Close
        </Button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noreferrer"
          onClick={onDone}
          className="inline-flex h-10 items-center rounded-md bg-ink-900 px-4 text-sm font-semibold text-parchment-50 hover:bg-ink-800 dark:bg-parchment-50 dark:text-ink-900"
        >
          Open WhatsApp
        </a>
      </div>
    </div>
  );
}
